import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { AppShell } from "@/components/AppShell";
import { listMarketplace } from "@/utils/lending.functions";
import { inr, riskColor } from "@/lib/lending-format";

export const Route = createFileRoute("/_authenticated/marketplace")({
  head: () => ({ meta: [
    { title: "Loan Marketplace — LendyTrack" },
    { name: "description", content: "Browse open borrower listings with risk grade, rate, and tenure." },
  ] }),
  component: MarketplacePage,
});

function MarketplacePage() {
  const q = useQuery({ queryKey: ["marketplace"], queryFn: () => listMarketplace() });
  const [grade, setGrade] = useState<"all" | "A" | "B" | "C">("all");
  const [maxRate, setMaxRate] = useState(25);

  const filtered = useMemo(() => {
    return (q.data ?? []).filter((l) => (grade === "all" || l.risk_grade === grade) && Number(l.annual_rate) <= maxRate);
  }, [q.data, grade, maxRate]);

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Marketplace</div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Open borrower listings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Filter by risk grade and rate. Funded share shows borrower demand.</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-4 mb-6 flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Grade</span>
          {(["all", "A", "B", "C"] as const).map((g) => (
            <button key={g} onClick={() => setGrade(g)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium border ${grade === g ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {g === "all" ? "All" : g}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 ml-auto">
          <span className="text-xs text-muted-foreground">Max rate</span>
          <input type="range" min={10} max={25} step={0.5} value={maxRate} onChange={(e) => setMaxRate(+e.target.value)} className="accent-primary w-40" />
          <span className="text-sm font-semibold">{maxRate}%</span>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((l) => {
          const pct = Math.min(100, Math.round((Number(l.funded_amount) / Number(l.amount)) * 100));
          return (
            <div key={l.id} className="rounded-xl border border-border bg-card p-5 shadow-soft">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-lg font-semibold">{l.borrower_alias}</div>
                  <div className="text-xs text-muted-foreground">{l.purpose} · {l.city ?? "—"}</div>
                </div>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${riskColor(l.risk_grade)}`}>
                  Grade {l.risk_grade}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
                <div><div className="text-xs text-muted-foreground">Amount</div><div className="font-semibold">{inr(l.amount)}</div></div>
                <div><div className="text-xs text-muted-foreground">Rate</div><div className="font-semibold text-success">{l.annual_rate}%</div></div>
                <div><div className="text-xs text-muted-foreground">Tenure</div><div className="font-semibold">{l.tenure_months} mo</div></div>
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Funded</span><span>{pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                </div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">{l.employment}</div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && !q.isLoading && (
        <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No listings match your filters.
        </div>
      )}
    </AppShell>
  );
}
